import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Home } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { logger } from "@/lib/logger";

interface AddCompDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  northId: string;
}

interface ExistingComp {
  id: string;
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  price: number | null;
}

export const AddCompDialog = ({ open, onOpenChange, northId }: AddCompDialogProps) => {
  const [searchUrl, setSearchUrl] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const queryClient = useQueryClient();

  // Fetch existing comps for the north
  const { data: existingComps } = useQuery({
    queryKey: ['comps', northId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('comparable_properties')
        .select('*')
        .eq('north_id', northId);

      if (error) throw error;
      return data as ExistingComp[];
    },
    enabled: open,
  });

  const addComp = useMutation({
    mutationFn: async (zillowUrl: string) => {
      // Step 1: Fetch property details from scrape-zillow
      const { data: propertyData, error: scrapeError } = await supabase.functions.invoke('scrape-zillow', {
        body: { zillowUrl }
      });

      if (scrapeError) throw scrapeError;
      if (!propertyData) throw new Error('No property data returned');

      // Step 2: Check visible comps count
      const { data: existingComps, error: countError } = await supabase
        .from('comparable_properties')
        .select('id, visible')
        .eq('north_id', northId);

      if (countError) throw countError;

      const visibleCount = existingComps?.filter(c => c.visible).length || 0;
      const shouldBeVisible = visibleCount < 5;

      // Step 3: Parse lot_size to extract numeric value
      let parsedLotSize = null;
      if (propertyData.lotSize) {
        if (typeof propertyData.lotSize === 'number') {
          parsedLotSize = propertyData.lotSize;
        } else if (typeof propertyData.lotSize === 'string') {
          // Extract first number from string like "0.41 Acres"
          const match = propertyData.lotSize.match(/[\d.]+/);
          parsedLotSize = match ? parseFloat(match[0]) : null;
        }
      }

      // Step 4: Insert the property as a comp
      const { error: insertError } = await supabase
        .from('comparable_properties')
        .insert({
          north_id: northId,
          address: propertyData.address,
          city: propertyData.city,
          state: propertyData.state,
          zip: propertyData.zip,
          price: propertyData.price,
          bedrooms: propertyData.bedrooms,
          bathrooms: propertyData.bathrooms,
          square_feet: propertyData.squareFeet,
          lot_size: parsedLotSize,
          property_type: propertyData.propertyType,
          zillow_url: zillowUrl,
          image_url: propertyData.imageUrl,
          status: 'FOR_SALE', // Default status for manually added comps
          days_on_market: propertyData.daysOnZillow,
          price_per_sqft: propertyData.squareFeet && propertyData.price
            ? Math.round(propertyData.price / propertyData.squareFeet)
            : null,
          visible: shouldBeVisible, // Only visible if under 5
        });

      if (insertError) throw insertError;

      return { propertyData, wasVisible: shouldBeVisible };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['comps', northId] });
      const visibilityMessage = data.wasVisible
        ? ' (visible on report)'
        : ' (hidden - max 5 visible reached)';
      toast.success(`Added ${data.propertyData.address}${visibilityMessage}`);
      setSearchUrl("");
    },
    onError: (error) => {
      logger.error('Error adding comp:', error);
      toast.error('Failed to add comparable property');
    },
  });

  const handleSearch = async () => {
    if (!searchUrl.trim()) {
      toast.error('Please enter a listing URL');
      return;
    }
    
    if (!searchUrl.includes('zillow.com')) {
      toast.error('Please enter a valid listing URL');
      return;
    }
    
    setIsSearching(true);
    try {
      await addComp.mutateAsync(searchUrl);
    } finally {
      setIsSearching(false);
    }
  };

  const formatPrice = (price: number | null) => {
    if (!price) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            Add Comparable
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Listing URL <span className="text-destructive">*</span>
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="https://www.zillow.com/homedetails/..."
                value={searchUrl}
                onChange={(e) => setSearchUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isSearching) {
                    handleSearch();
                  }
                }}
              />
              <Button 
                onClick={handleSearch} 
                disabled={!searchUrl.trim() || isSearching}
              >
                <Search className="h-4 w-4 mr-2" />
                {isSearching ? 'Adding...' : 'Add Listing'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Paste a listing URL to add it as a comparable property
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-medium">
              Current Comparable Listings ({existingComps?.length || 0})
            </h3>

            {existingComps && existingComps.length > 0 ? (
              <div className="space-y-2">
                {existingComps.map((comp) => (
                  <Card key={comp.id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">{comp.address}</h4>
                        <p className="text-sm text-muted-foreground">
                          {comp.city}, {comp.state} {comp.zip}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatPrice(comp.price)}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Home className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No comparable listings yet</p>
                <p className="text-sm">Search for a property address to add one</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
